// frontend/src/modules/guias/pages/Guias.js
import React, { useState, useEffect } from 'react';
import { FaPlus, FaEye, FaPrint, FaEdit, FaTrash, FaCheck, FaSearch, FaTimes, FaFilePdf, FaMoneyBill } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

function Guias({ usuario }) {
  const empresaId = usuario?.empresa_id || 1;
  const navigate = useNavigate();
  const [guias, setGuias] = useState([]);
  const [guiasFiltradas, setGuiasFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    tipo: '',
    competencia: '',
    data_vencimento: '',
    valor: '',
    descricao: '',
    observacao: '',
    codigo_receita: '',
    campos_extras: {}
  });

  const isMobile = useMediaQuery({ maxWidth: 768 });
  const token = localStorage.getItem('token');

  const fetchGuias = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/empresa/${empresaId}/guias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erro ao buscar guias');
      const data = await response.json();
      setGuias(data);
      setGuiasFiltradas(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuias();
  }, []);

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (term.trim() === '') {
      setGuiasFiltradas(guias);
      return;
    }

    const filtradas = guias.filter(g => {
      const tipo = (g.tipo || '').toLowerCase();
      const descricao = (g.descricao || '').toLowerCase();
      const status = (g.status || '').toLowerCase();
      const competencia = (g.competencia || '').toLowerCase();

      return tipo.includes(term) ||
             descricao.includes(term) ||
             status.includes(term) ||
             competencia.includes(term);
    });

    setGuiasFiltradas(filtradas);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setGuiasFiltradas(guias);
  };

  const handlePrint = (guiaId) => {
    const printWindow = window.open(`/guia/${guiaId}?print=true`, '_blank');
    if (printWindow) {
      printWindow.onload = function() {
        setTimeout(() => {
          printWindow.print();
        }, 1000);
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/guia', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          valor: parseFloat(formData.valor) || 0
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao criar guia');
      }

      setSuccess('✅ Guia criada com sucesso!');
      setMostrarForm(false);
      setFormData({
        tipo: '',
        competencia: '',
        data_vencimento: '',
        valor: '',
        descricao: '',
        observacao: '',
        codigo_receita: '',
        campos_extras: {}
      });
      fetchGuias();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmarPagamento = async (id) => {
    if (!window.confirm('Confirmar pagamento desta guia?')) return;
    try {
      const response = await fetch(`/api/guia/${id}/pagar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          data_pagamento: new Date().toISOString().split('T')[0]
        })
      });
      if (!response.ok) throw new Error('Erro ao confirmar pagamento');
      setSuccess('✅ Pagamento confirmado!');
      fetchGuias();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      'DARF': 'DARF - Imposto de Renda',
      'GPS': 'GPS - INSS',
      'GRP': 'GRP - FGTS',
      'DAS': 'DAS - Simples Nacional',
      'GRU': 'GRU - Taxas e Emolumentos',
      'GARE': 'GARE - ICMS/ISS',
      'OUTRO': 'Outro'
    };
    return tipos[tipo] || tipo;
  };

  const getStatusColor = (status) => {
    const colors = {
      'PENDENTE': '#fff3cd',
      'PAGO': '#d4edda',
      'VENCIDO': '#f8d7da',
      'CANCELADO': '#e2e3e5'
    };
    return colors[status] || '#e9ecef';
  };

  const getStatusColorText = (status) => {
    const colors = {
      'PENDENTE': '#856404',
      'PAGO': '#155724',
      'VENCIDO': '#721c24',
      'CANCELADO': '#383d41'
    };
    return colors[status] || '#6c757d';
  };

  const getTipoColor = (tipo) => {
    const colors = {
      'DARF': '#dc3545',
      'GPS': '#007bff',
      'GRP': '#28a745',
      'DAS': '#6f42c1',
      'GRU': '#fd7e14',
      'GARE': '#17a2b8'
    };
    return colors[tipo] || '#6c757d';
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '50px' }}>🔄 Carregando...</div>;

  return (
    <div style={{ padding: isMobile ? '10px' : '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Cabeçalho */}
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        marginBottom: '20px',
        gap: '10px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '28px' }}>📄 Guias de Pagamento</h2>
          <p style={{ color: '#6c757d', margin: '5px 0 0 0', fontSize: isMobile ? '13px' : '14px' }}>
            Gerencie DARF, GPS, GRP, DAS, GRU e GARE
          </p>
        </div>
        <button 
          onClick={() => setMostrarForm(!mostrarForm)}
          style={{ 
            padding: isMobile ? '8px 16px' : '10px 20px', 
            backgroundColor: mostrarForm ? '#dc3545' : '#28a745', 
            color: 'white', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer',
            fontSize: isMobile ? '13px' : '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            justifyContent: 'center',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          {mostrarForm ? <FaTimes /> : <FaPlus />} {mostrarForm ? 'Cancelar' : 'Nova Guia'}
        </button>
      </div>

      {/* Mensagens */}
      {error && <div style={{ backgroundColor: '#ffebee', padding: '10px', borderRadius: '4px', color: '#c62828', marginBottom: '15px', fontSize: isMobile ? '13px' : '14px' }}>❌ {error}</div>}
      {success && <div style={{ backgroundColor: '#e8f5e9', padding: '10px', borderRadius: '4px', color: '#2e7d32', marginBottom: '15px', fontSize: isMobile ? '13px' : '14px' }}>✅ {success}</div>}

      {/* Barra de Busca */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: isMobile ? '12px' : '15px', 
        borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: isMobile ? '150px' : '250px' }}>
            <input
              type="text"
              placeholder="🔍 Buscar por tipo, descrição, status..."
              value={searchTerm}
              onChange={handleSearch}
              style={{
                width: '100%',
                padding: isMobile ? '8px 12px' : '10px 15px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: isMobile ? '13px' : '14px'
              }}
            />
          </div>
          {searchTerm && (
            <button 
              onClick={handleClearSearch}
              style={{
                padding: isMobile ? '6px 12px' : '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: isMobile ? '12px' : '14px'
              }}
            >
              <FaTimes /> Limpar
            </button>
          )}
          <span style={{ marginLeft: 'auto', color: '#6c757d', fontSize: isMobile ? '12px' : '14px' }}>
            {guiasFiltradas.length} guia(s)
          </span>
        </div>
      </div>

      {/* Formulário */}
      {mostrarForm && (
        <form onSubmit={handleSubmit} style={{ backgroundColor: '#f8f9fa', padding: isMobile ? '15px' : '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: isMobile ? '16px' : '18px' }}>📝 Gerar Guia de Pagamento</h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '15px' }}>
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Tipo de Guia *</label>
              <select 
                value={formData.tipo} 
                onChange={(e) => setFormData({...formData, tipo: e.target.value})}
                required
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}
              >
                <option value="">Selecione...</option>
                <option value="DARF">DARF - Imposto de Renda</option>
                <option value="GPS">GPS - INSS</option>
                <option value="GRP">GRP - FGTS</option>
                <option value="DAS">DAS - Simples Nacional</option>
                <option value="GRU">GRU - Taxas e Emolumentos</option>
                <option value="GARE">GARE - ICMS/ISS</option>
                <option value="OUTRO">Outro</option>
              </select>
            </div>
{/* Mês e Ano - Competência */}
<div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '15px' }}>
  
  {/* Mês */}
  <div>
    <label style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '500' }}>
      Mês de Competência *
    </label>
    <select
      value={formData.mes_competencia}
      onChange={(e) => setFormData({ ...formData, mes_competencia: e.target.value })}
      required
      style={{
        width: '100%',
        padding: '8px',
        marginTop: '5px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: isMobile ? '13px' : '14px',
        backgroundColor: 'white',
        cursor: 'pointer',
        transition: 'border-color 0.2s ease',
        outline: 'none'
      }}
      onFocus={(e) => {
        e.target.style.borderColor = '#007bff';
        e.target.style.boxShadow = '0 0 0 3px rgba(0,123,255,0.1)';
      }}
      onBlur={(e) => {
        e.target.style.borderColor = '#ddd';
        e.target.style.boxShadow = 'none';
      }}
    >
      <option value="">Selecione...</option>
      <option value="1">Janeiro</option>
      <option value="2">Fevereiro</option>
      <option value="3">Março</option>
      <option value="4">Abril</option>
      <option value="5">Maio</option>
      <option value="6">Junho</option>
      <option value="7">Julho</option>
      <option value="8">Agosto</option>
      <option value="9">Setembro</option>
      <option value="10">Outubro</option>
      <option value="11">Novembro</option>
      <option value="12">Dezembro</option>
    </select>
  </div>

  {/* Ano */}
  <div>
    <label style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '500' }}>
      Ano de Competência *
    </label>
    <select
      value={formData.ano_competencia}
      onChange={(e) => setFormData({ ...formData, ano_competencia: e.target.value })}
      required
      style={{
        width: '100%',
        padding: '8px',
        marginTop: '5px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: isMobile ? '13px' : '14px',
        backgroundColor: 'white',
        cursor: 'pointer',
        transition: 'border-color 0.2s ease',
        outline: 'none'
      }}
      onFocus={(e) => {
        e.target.style.borderColor = '#007bff';
        e.target.style.boxShadow = '0 0 0 3px rgba(0,123,255,0.1)';
      }}
      onBlur={(e) => {
        e.target.style.borderColor = '#ddd';
        e.target.style.boxShadow = 'none';
      }}
    >
      <option value="">Selecione...</option>
      {[2024, 2025, 2026, 2027].map(ano => (
        <option key={ano} value={ano}>{ano}</option>
      ))}
    </select>
  </div>
</div>

{/* Preview da Competência (opcional - mostra como ficará salvo) */}
{formData.mes_competencia && formData.ano_competencia && (
  <div style={{
    marginTop: '10px',
    padding: '10px 15px',
    backgroundColor: '#e3f2fd',
    borderRadius: '4px',
    fontSize: isMobile ? '12px' : '13px',
    color: '#1565c0',
    borderLeft: '4px solid #007bff'
  }}>
    📅 Competência selecionada: <strong>
      {['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][formData.mes_competencia]
      } de {formData.ano_competencia}
    </strong>
  </div>
)}
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Data de Vencimento *</label>
              <input 
                type="date"
                value={formData.data_vencimento}
                onChange={(e) => setFormData({...formData, data_vencimento: e.target.value})}
                required
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Valor (R$) *</label>
              <input 
                type="number"
                step="0.01"
                value={formData.valor}
                onChange={(e) => setFormData({...formData, valor: e.target.value})}
                required
                placeholder="0,00"
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}
              />
            </div>
			
{/* Código da Receita */}
<div>
  <label style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '500' }}>
    Código da Receita
  </label>
  
  {/* Select com códigos principais */}
  <select
    value={formData.codigo_receita}
    onChange={(e) => setFormData({ ...formData, codigo_receita: e.target.value })}
    style={{
      width: '100%',
      padding: '8px',
      marginTop: '5px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: isMobile ? '13px' : '14px',
      backgroundColor: 'white',
      cursor: 'pointer',
      transition: 'border-color 0.2s ease',
      outline: 'none'
    }}
    onFocus={(e) => {
      e.target.style.borderColor = '#007bff';
      e.target.style.boxShadow = '0 0 0 3px rgba(0,123,255,0.1)';
    }}
    onBlur={(e) => {
      e.target.style.borderColor = '#ddd';
      e.target.style.boxShadow = 'none';
    }}
  >
    <option value="">Selecione ou digite abaixo...</option>

    {/* ============================================ */}
    {/* IMPOSTO DE RENDA (IR) */}
    {/* ============================================ */}
    <optgroup label="💰 Imposto de Renda (IR)">
      <option value="0561">0561 - IRRF - Trabalho Assalariado</option>
      <option value="0588">0588 - IRRF - Autônomo</option>
      <option value="1708">1708 - IRPF - Ganhos de Capital</option>
      <option value="0190">0190 - IRPJ - Lucro Real</option>
      <option value="2089">2089 - IRPJ - Lucro Presumido</option>
      <option value="0220">0220 - IRPJ - Lucro Arbitrado</option>
    </optgroup>

    {/* ============================================ */}
    {/* INSS / PREVIDÊNCIA */}
    {/* ============================================ */}
    <optgroup label="👥 INSS / Previdência">
      <option value="1138">1138 - Contribuição Previdenciária - Empregador</option>
      <option value="1163">1163 - Contribuição Previdenciária - Segurado</option>
      <option value="1104">1104 - Contribuição Previdenciária - Autônomo</option>
      <option value="1120">1120 - Contribuição Previdenciária - Doméstico</option>
      <option value="1137">1137 - Contribuição Previdenciária - Empregador Doméstico</option>
    </optgroup>

    {/* ============================================ */}
    {/* SIMPLES NACIONAL */}
    {/* ============================================ */}
    <optgroup label="📊 Simples Nacional">
      <option value="6106">6106 - DAS - Simples Nacional</option>
      <option value="6107">6107 - DAS - MEI</option>
      <option value="6108">6108 - DAS - MEI - Parcelamento</option>
      <option value="6110">6110 - Simples Nacional - Receita Bruta</option>
    </optgroup>

    {/* ============================================ */}
    {/* FGTS */}
    {/* ============================================ */}
    <optgroup label="💼 FGTS">
      <option value="1330">1330 - FGTS - Recolhimento Mensal</option>
      <option value="1350">1350 - FGTS - Rescisão</option>
      <option value="1340">1340 - FGTS - Parcelamento</option>
    </optgroup>

    {/* ============================================ */}
    {/* CSLL / COFINS / PIS */}
    {/* ============================================ */}
    <optgroup label="💵 CSLL / PIS / COFINS">
      <option value="5952">5952 - CSLL/Cofins/PIS - Retenção PJ para PJ</option>
      <option value="5979">5979 - CSLL/Cofins/PIS - Retenção Órgão Público</option>
      <option value="2484">2484 - CSLL - Lucro Real</option>
      <option value="2372">2372 - CSLL - Lucro Presumido</option>
      <option value="2089">2089 - PIS/COFINS - Lucro Presumido</option>
      <option value="6912">6912 - PIS/COFINS - Lucro Real</option>
    </optgroup>

    {/* ============================================ */}
    {/* ICMS / ISS */}
    {/* ============================================ */}
    <optgroup label="🏪 ICMS / ISS">
      <option value="1001">1001 - ICMS - Substituição Tributária</option>
      <option value="1010">1010 - ICMS - Recolhimento Mensal</option>
      <option value="1002">1002 - ICMS - Importação</option>
      <option value="1011">1011 - ICMS - Diferencial de Alíquota</option>
    </optgroup>

    {/* ============================================ */}
    {/* IPI / II */}
    {/* ============================================ */}
    <optgroup label="🏭 IPI / Importação">
      <option value="5123">5123 - IPI - Recolhimento Mensal</option>
      <option value="5124">5124 - IPI - Importação</option>
      <option value="5100">5100 - II - Imposto de Importação</option>
    </optgroup>

    {/* ============================================ */}
    {/* TAXAS E OUTROS */}
    {/* ============================================ */}
    <optgroup label="📋 Taxas e Outros">
      <option value="9051">9051 - Taxa - DARF Genérico</option>
      <option value="8414">8414 - Taxa - Emolumentos</option>
      <option value="9197">9197 - Multas e Juros</option>
      <option value="4500">4500 - Multas - Receita Federal</option>
      <option value="5265">5265 - Juros - Receita Federal</option>
    </optgroup>

    {/* ============================================ */}
    {/* PARCELAMENTOS */}
    {/* ============================================ */}
    <optgroup label="📅 Parcelamentos">
      <option value="4270">4270 - Parcelamento - Lei 11.941/2009</option>
      <option value="4340">4340 - Parcelamento - Lei 12.865/2013</option>
      <option value="4400">4400 - Parcelamento - Lei 13.496/2017</option>
      <option value="4415">4415 - Parcelamento - Simples Nacional</option>
    </optgroup>

  </select>

  {/* Campo para digitar código personalizado (opcional) */}
  <input
    type="text"
    value={formData.codigo_receita}
    onChange={(e) => setFormData({ ...formData, codigo_receita: e.target.value })}
    placeholder="Ou digite o código manualmente (ex: 6106)"
    style={{
      width: '100%',
      padding: '8px',
      marginTop: '8px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: isMobile ? '13px' : '14px',
      transition: 'border-color 0.2s ease',
      outline: 'none'
    }}
    onFocus={(e) => {
      e.target.style.borderColor = '#007bff';
      e.target.style.boxShadow = '0 0 0 3px rgba(0,123,255,0.1)';
    }}
    onBlur={(e) => {
      e.target.style.borderColor = '#ddd';
      e.target.style.boxShadow = 'none';
    }}
  />

  {/* Preview do código selecionado */}
  {formData.codigo_receita && (
    <div style={{
      marginTop: '8px',
      padding: '8px 12px',
      backgroundColor: '#e3f2fd',
      borderRadius: '4px',
      fontSize: isMobile ? '11px' : '12px',
      color: '#1565c0',
      borderLeft: '3px solid #007bff'
    }}>
      ✅ Código selecionado: <strong>{formData.codigo_receita}</strong>
    </div>
  )}
</div>
			
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Descrição</label>
              <input 
                type="text"
                value={formData.descricao}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                placeholder="Descrição da guia"
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Observação</label>
              <textarea 
                value={formData.observacao}
                onChange={(e) => setFormData({...formData, observacao: e.target.value})}
                rows="2"
                placeholder="Observações adicionais"
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}
              />
            </div>
          </div>
          <div style={{ marginTop: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button type="submit" disabled={loading} style={{ 
              padding: isMobile ? '8px 20px' : '10px 30px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer',
              fontSize: isMobile ? '13px' : '14px',
              flex: isMobile ? '1' : '0',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              justifyContent: 'center'
            }}>
              <FaFilePdf /> {loading ? 'Gerando...' : 'Gerar Guia'}
            </button>
            <button 
              type="button" 
              onClick={() => { setMostrarForm(false); }}
              style={{ 
                padding: isMobile ? '8px 20px' : '10px 30px', 
                backgroundColor: '#6c757d', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontSize: isMobile ? '13px' : '14px',
                flex: isMobile ? '1' : '0',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                justifyContent: 'center'
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Lista de Guias */}
      <div style={{ backgroundColor: 'white', padding: isMobile ? '10px' : '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        {guiasFiltradas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: isMobile ? '30px' : '40px' }}>
            <div style={{ fontSize: isMobile ? '36px' : '48px', marginBottom: '10px' }}>
              {searchTerm ? '🔍' : '📭'}
            </div>
            <p style={{ color: '#6c757d', fontSize: isMobile ? '13px' : '14px' }}>
              {searchTerm ? `Nenhuma guia encontrada para "${searchTerm}"` : 'Nenhuma guia registrada'}
            </p>
          </div>
        ) : isMobile ? (
          // ============================================
          // MOBILE - CARDS
          // ============================================
          <div>
            {guiasFiltradas.map(g => (
              <div
                key={g.id}
                style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '10px',
                  borderLeft: `4px solid ${getTipoColor(g.tipo)}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '10px',
                    backgroundColor: getTipoColor(g.tipo) + '20',
                    color: getTipoColor(g.tipo),
                    fontWeight: 'bold',
                    fontSize: '11px'
                  }}>
                    {g.tipo}
                  </span>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '10px',
                    backgroundColor: getStatusColor(g.status),
                    color: getStatusColorText(g.status),
                    fontSize: '10px'
                  }}>
                    {g.status}
                  </span>
                </div>
                <div style={{ fontSize: '13px', fontWeight: 'bold' }}>
                  {g.competencia}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                  <span>📅 {formatDate(g.data_vencimento)}</span>
                  <span style={{ fontWeight: 'bold', color: '#007bff' }}>{formatCurrency(g.valor)}</span>
                </div>
                <div style={{ marginTop: '8px', display: 'flex', gap: '6px', justifyContent: 'flex-end', borderTop: '1px solid #dee2e6', paddingTop: '8px' }}>
                  <button 
                    onClick={() => navigate(`/guia/${g.id}`)}
                    style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer' }}
                    title="Ver Detalhes"
                  >
                    <FaEye size={14} />
                  </button>
                  <button 
                    onClick={() => handlePrint(g.id)}
                    style={{ background: 'none', border: 'none', color: '#28a745', cursor: 'pointer' }}
                    title="Imprimir"
                  >
                    <FaPrint size={14} />
                  </button>
                  {g.status === 'PENDENTE' && (
                    <button 
                      onClick={() => handleConfirmarPagamento(g.id)}
                      style={{ background: 'none', border: 'none', color: '#28a745', cursor: 'pointer' }}
                      title="Confirmar Pagamento"
                    >
                      <FaCheck size={14} />
                    </button>
                  )}
                  <button 
                    style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                    title="Excluir"
                    onClick={async () => {
                      if (!window.confirm('Tem certeza que deseja excluir esta guia?')) return;
                      try {
                        const response = await fetch(`/api/guia/${g.id}`, {
                          method: 'DELETE',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (!response.ok) throw new Error('Erro ao excluir guia');
                        setSuccess('✅ Guia excluída com sucesso!');
                        fetchGuias();
                        setTimeout(() => setSuccess(''), 3000);
                      } catch (err) {
                        setError(err.message);
                      }
                    }}
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // ============================================
          // DESKTOP - TABELA
          // ============================================
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>ID</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Tipo</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Competência</th>
                <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Valor</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Vencimento</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {guiasFiltradas.map(g => (
                <tr key={g.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '10px' }}>{g.id}</td>
                  <td style={{ padding: '10px' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      backgroundColor: getTipoColor(g.tipo) + '20',
                      color: getTipoColor(g.tipo),
                      fontWeight: 'bold',
                      fontSize: '12px'
                    }}>
                      {g.tipo}
                    </span>
                  </td>
                  <td style={{ padding: '10px' }}>{g.competencia}</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>
                    {formatCurrency(g.valor)}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    {formatDate(g.data_vencimento)}
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      backgroundColor: getStatusColor(g.status),
                      color: getStatusColorText(g.status)
                    }}>
                      {g.status}
                    </span>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button 
                        onClick={() => navigate(`/guia/${g.id}`)}
                        style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer' }}
                        title="Ver Detalhes"
                      >
                        <FaEye size={14} />
                      </button>
                      <button 
                        onClick={() => handlePrint(g.id)}
                        style={{ background: 'none', border: 'none', color: '#28a745', cursor: 'pointer' }}
                        title="Imprimir"
                      >
                        <FaPrint size={14} />
                      </button>
                      {g.status === 'PENDENTE' && (
                        <button 
                          onClick={() => handleConfirmarPagamento(g.id)}
                          style={{ background: 'none', border: 'none', color: '#28a745', cursor: 'pointer' }}
                          title="Confirmar Pagamento"
                        >
                          <FaCheck size={14} />
                        </button>
                      )}
                      <button 
                        style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }}
                        title="Excluir"
                        onClick={async () => {
                          if (!window.confirm('Tem certeza que deseja excluir esta guia?')) return;
                          try {
                            const response = await fetch(`/api/guia/${g.id}`, {
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (!response.ok) throw new Error('Erro ao excluir guia');
                            setSuccess('✅ Guia excluída com sucesso!');
                            fetchGuias();
                            setTimeout(() => setSuccess(''), 3000);
                          } catch (err) {
                            setError(err.message);
                          }
                        }}
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Guias;